import { useEffect, useState, useMemo, createContext, useContext } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from "@hooks/useAuth";
import { ADMIN_ROLE, ADMIN_SCHOOL, ADMIN_CLASS } from "@constants/roleconstants";
import { Bell } from "lucide-react";
import { UserProfile } from '@components/shares';
import Menu, { MenuProps } from 'antd/es/menu';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import logo from "@assets/images/logo.png"
import { useWindowSize } from 'usehooks-ts'
import LanguageSelector from '@components/shares/LanguageSelector';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import blankAvatar from '@assets/images/blankavt.jpg';
import { adminICDLMenu, adminSchoolMenu, teacherMenu } from '@constants/ menuconstants';
import './index.css';
import { useTranslation } from 'react-i18next';
import { useSessionWatcher } from '@hooks/useSessionWatcher';
import { DashboardSnapshotSyncProvider } from '@features/admin/dashboard/DashboardSnapshotSyncProvider';

// Create a context to share calendar date across components
export const CalendarContext = createContext({
  selectedDate: new Date(),
  setSelectedDate: (date: Date) => { },
  calendarView: 'week',
  setCalendarView: (view: string) => { },
});

export const useCalendarContext = () => useContext(CalendarContext);

// Sidebar component with calendar
const SideBar = ({ collapsed, setCollapsed }: { collapsed: boolean, setCollapsed: (v: boolean) => void }) => {
  const { width = 0 } = useWindowSize()
  const location = useLocation();
  const { auth } = useAuth();
  
  const { t } = useTranslation()
  const isSmallScreenSize = width < 1000;

  const getCurrentKeyFromPath = (pathname: string): string => {
    const segments = pathname.split('/').filter(Boolean);
    const page = segments[1] || segments[0] || 'dashboard';

    const subRouteToMenuKey: Record<string, string> = {
      // class sub-routes
      'classdetail': 'class',
      'newclass': 'class',
      'module': 'modules',
      'newmodule': 'modules',
      'module-management': 'modules',
      'newschool': 'school',
      'detail': 'school',
      'accountdetail': 'account',
      'newaccount': 'account',
      'roledetail': 'role',
      'newrole': 'role',
    };

    const subPage = segments[2];
    if (subPage && subRouteToMenuKey[subPage]) return subRouteToMenuKey[subPage];
    if (subRouteToMenuKey[page]) return subRouteToMenuKey[page];

    return page;
  };

  const [current, setCurrent] = useState(
    getCurrentKeyFromPath(location.pathname) || sessionStorage.getItem('selectedKey') || 'dashboard'
  );

  useEffect(() => {
    const key = getCurrentKeyFromPath(location.pathname);
    if (key) {
      setCurrent(key);
      sessionStorage.setItem('selectedKey', key);
    }
  }, [location.pathname]);
  const onClick: MenuProps['onClick'] = (e) => {
    sessionStorage.setItem('selectedKey', e.key)
    setCurrent(e.key);
  };
  const handleChangeCollapseMenu = () => {
    setCollapsed(!collapsed);
  }
  useEffect(() => {
    const handleMenuKeyChange = (event: CustomEvent) => {
      const newKey = event.detail?.key;
      if (newKey) {
        setCurrent(newKey);
      }
    };

    // Listen for custom event when menu key changes
    window.addEventListener('menuKeyChange', handleMenuKeyChange as EventListener);

    return () => {
      window.removeEventListener('menuKeyChange', handleMenuKeyChange as EventListener);
    };
  }, []);
  

  const generateMenuByRole = useMemo(() => {
    switch (auth.role) {
      case ADMIN_ROLE:
        return adminICDLMenu(t);
      case ADMIN_SCHOOL:
        return adminSchoolMenu(t);
      case ADMIN_CLASS:
        return teacherMenu(t);
      default:
        return adminICDLMenu(t);
    }
  }, [auth.role, t])

  // Tìm chuỗi key menu cha chứa key đang chọn để tự mở submenu tương ứng
  const findParentKeys = (items: any[], target: string, parents: string[] = []): string[] | null => {
    for (const item of items || []) {
      if (!item) continue;
      if (item.key === target) return parents;
      if (item.children) {
        const found = findParentKeys(item.children, target, [...parents, String(item.key)]);
        if (found) return found;
      }
    }
    return null;
  };

  const [openKeys, setOpenKeys] = useState<string[]>([]);

  useEffect(() => {
    const parents = findParentKeys(generateMenuByRole as any[], current) || [];
    if (parents.length) {
      setOpenKeys((prev) => Array.from(new Set([...prev, ...parents])));
    }
  }, [current, generateMenuByRole]);

  const isMenuCollapsed = collapsed || isSmallScreenSize;

  return (
    <aside className={`h-full`}>
      <nav className="h-full overflow-y-auto flex flex-col bg-white border-r custom-scrollbar-hidden shadow-sm relative">
        <div className="p-4 pb-5 flex justify-center">
          <img src={logo} className={`overflow-hidden transition-all ${collapsed || isSmallScreenSize ? "w-0" : "w-30"}`} />
        </div>
        <div className="layout-admin-sider-menu">
          <Menu
            onClick={onClick}
            selectedKeys={[current]}
            mode="inline"
            items={generateMenuByRole}
            inlineCollapsed={isMenuCollapsed}
            {...(!isMenuCollapsed && {
              openKeys,
              onOpenChange: (keys: string[]) => setOpenKeys(keys),
            })}
          />
        </div>
        <button
          className={`bg-primary border-none absolute bottom-5 py-1 pl-2 pr-1 z-10 ${
            collapsed || isSmallScreenSize
              ? 'right-0 rounded-l-sm'
              : 'right-0 rounded-r-sm'
          }`}
          onClick={handleChangeCollapseMenu}
        >
          {collapsed || isSmallScreenSize ? (
            <MenuFoldOutlined className='text-white ' />
          ) : (
            <MenuUnfoldOutlined className='text-white' />
          )}
        </button>
        <div className={`flex ${collapsed || isSmallScreenSize ? "w-[50px]" : "w-52 ml-5"}`}></div>
      </nav>
    </aside>
  )
}

const CONTENT_AREA_TYPE_1_PATHS = [
  '/reporting_practice',
  '/reporting_exams',
  '/admin/school',
];

export const LayoutAdmin: React.FC = () => {
  const location = useLocation();
  const isCalendarPage = location.pathname.includes('/calendar');
  const isContentAreaType1 = CONTENT_AREA_TYPE_1_PATHS.some((path) =>
    location.pathname.includes(path),
  );
  const { auth } = useAuth();
  const navigate = useNavigate();
  const { height = 0 } = useWindowSize();
  const [collapsed, setCollapsed] = useState(false);

  useSessionWatcher();

  // Calendar context state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState('week');

  useEffect(() => {
    if (!auth.access_token) {
      navigate('/user/login');
    }
  }, [auth.access_token]);

  const generateRole = useMemo(() => {
    switch (auth.role) {
      case ADMIN_ROLE:
        return 'Admin';
        break;
      case ADMIN_SCHOOL:
        return 'Admin school';
        break;
      case ADMIN_CLASS:
        return 'Giáo viên';
      default:
        return 'Admin';
        break;
    }
  }, [auth.role])

  return (
    <CalendarContext.Provider value={{ selectedDate, setSelectedDate, calendarView, setCalendarView }}>
      <DashboardSnapshotSyncProvider>
      <div className="flex h-screen">
        <SideBar collapsed={collapsed} setCollapsed={setCollapsed} />
        <div className={`${collapsed ? 'w-[calc(100%-80px)]' : 'w-[calc(100%-228px)]'} flex flex-1 flex-col`}>
          <div className='w-full h-[70px] flex flex-row items-center justify-end shadow' >
            <div className='flex flex-row items-center mr-20 h-[90px]' >
              {/* <div className='relative p-3 rounded-full hover:bg-success/10 hover:text-success cursor-pointer text-gray-500 mr-8'>
                <Bell size={20} />
              </div> */}
              <div className='mr-12 '><LanguageSelector /></div>
              <UserProfile
                imageUrl={auth?.avatar_url ?? blankAvatar}
                avatarSize='md'
                userName={auth?.full_name}
                userRole={generateRole}
              />
            </div>
          </div>
          {isContentAreaType1 ? (
            <div
              className="flex min-h-0 flex-1 flex-col overflow-hidden bg-gray-50 p-4"
            >
              <Outlet />
            </div>
          ) : (
            <div
              className={`px-[30px] py-[40px] w-full ${isCalendarPage ? 'no-scroll' : 'overflow-auto'}`}
              style={{ height: `${height - 70}px` }}
            >
              <Outlet />
            </div>
          )}
        </div>
      </div>
      </DashboardSnapshotSyncProvider>
    </CalendarContext.Provider>
  );
};



